import type { MrpForecastCell, MrpForecastSeries } from "@/lib/mrp";

const FORECAST_QUANTITY_ROUND_EPS = 1e-9;

function roundUpWholeUnit(value: number): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  if (parsed >= 0) return Math.ceil(parsed - FORECAST_QUANTITY_ROUND_EPS);
  return Math.floor(parsed + FORECAST_QUANTITY_ROUND_EPS);
}

/** Spreadsheet: ROUNDUP(deficit / multiple, 0) * multiple when balance before order is negative. */
function orderQtyToRestoreNonNegative(balanceBeforeOrder: number, orderMultiple: number): number {
  if (balanceBeforeOrder >= -1e-9) return 0;
  const deficit = -balanceBeforeOrder;
  const multiple = Math.max(1, orderMultiple);
  return Math.ceil(deficit / multiple) * multiple;
}

/**
 * Recompute monthly roll-forward for one location row after a reserve change.
 * Demand and arrivals are taken from the existing cells (unchanged by reserve edits).
 */
export function rerollMrpForecastSeriesCells(args: {
  cells: MrpForecastCell[];
  orderMultiple: number;
  startingQty: number;
  allocatedReserve: number;
}): MrpForecastCell[] {
  const nextCells: MrpForecastCell[] = [];
  let runningStart = roundUpWholeUnit(args.startingQty);
  for (const priorCell of args.cells) {
    const starting_qty = runningStart;
    const arrivals = roundUpWholeUnit(priorCell.arrivals);
    const demand = roundUpWholeUnit(priorCell.demand);
    const balanceBeforeOrder = starting_qty + arrivals - demand;
    const order_qty = orderQtyToRestoreNonNegative(balanceBeforeOrder, args.orderMultiple);
    const ending_qty = roundUpWholeUnit(balanceBeforeOrder + order_qty);
    const shortfall = Math.max(0, -balanceBeforeOrder);
    nextCells.push({
      ...priorCell,
      month: priorCell.month,
      starting_qty,
      arrivals,
      demand,
      ending_qty,
      reserve_target: args.allocatedReserve,
      shortfall,
      order_qty,
    });
    runningStart = ending_qty;
  }
  return nextCells;
}

/** Apply a site reserve edit to one forecast location row and sync CPN-wide reserve totals. */
export function applyMrpForecastReserveChange(
  series: MrpForecastSeries[],
  cpn: string,
  locationCode: string,
  newAllocatedReserve: number,
): { series: MrpForecastSeries[]; patched: boolean } {
  const targetSeries = series.find(
    (row) => row.cpn === cpn && row.location_code === locationCode,
  );
  if (targetSeries == null) {
    return { series, patched: false };
  }

  const oldAllocated = targetSeries.allocated_reserve;
  if (oldAllocated === newAllocatedReserve) {
    return { series, patched: true };
  }

  const deltaReserveTotal = newAllocatedReserve - oldAllocated;
  const newReserveTotalForCpn = targetSeries.reserve_quantity + deltaReserveTotal;
  const newStartingQty = (targetSeries.cells[0]?.starting_qty ?? 0) + (oldAllocated - newAllocatedReserve);
  const rerolledCells = rerollMrpForecastSeriesCells({
    cells: targetSeries.cells,
    orderMultiple: targetSeries.order_multiple,
    startingQty: newStartingQty,
    allocatedReserve: newAllocatedReserve,
  });

  const nextSeries = series.map((row) => {
    if (row.cpn !== cpn) return row;
    if (row.location_code === locationCode) {
      return {
        ...row,
        allocated_reserve: newAllocatedReserve,
        reserve_quantity: newReserveTotalForCpn,
        cells: rerolledCells,
      };
    }
    return {
      ...row,
      reserve_quantity: newReserveTotalForCpn,
    };
  });

  return { series: nextSeries, patched: true };
}
